import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { UploadAsset, SaveTheme } from '../../../../utilities/DL/PersonalQuickReaction';
import { resolveAssetUrl } from '../../../../utilities/BL/PersonalQuickReaction';

/**
 * COMPONENTE: QuickReactionPersonalThemeModal
 * [HU-06] Modal táctil para la creación y edición de temas visuales para Reacción Rápida Personal.
 * 
 * Permite subir y asociar:
 * - Nombre del Tema
 * - Fondo (1920x1080)
 * - Logo Oficial
 * - Sprite 1 (Espalda)
 * - Sprite 2 (Frente)
 * - Sprite 3 (Disparo/Acción)
 * - Sprite 4 (Victoria)
 * - Cintillo 1536x240
 * - Cintillo 1920x300
 */
export const QuickReactionPersonalThemeModal = ({
    isOpen = false,
    onClose = () => {},
    roomId = '',
    initialTheme = null,
    onSaved = () => {}
}) => {
    const [name, setName] = useState('Tema Personalizado');
    const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
    const [logoImageUrl, setLogoImageUrl] = useState('');
    const [spriteBackUrl, setSpriteBackUrl] = useState('');
    const [spriteFrontUrl, setSpriteFrontUrl] = useState('');
    const [spriteShootUrl, setSpriteShootUrl] = useState('');
    const [spriteVictoryUrl, setSpriteVictoryUrl] = useState('');
    const [banner1536Url, setBanner1536Url] = useState('');
    const [banner1920Url, setBanner1920Url] = useState('');

    const [uploadingField, setUploadingField] = useState(null); // 'background', 'logo', etc.
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // Cargar datos iniciales si se edita un tema existente
    useEffect(() => {
        if (isOpen) {
            if (initialTheme) {
                setName(initialTheme.name || 'Tema Personalizado');
                setBackgroundImageUrl(initialTheme.backgroundImageUrl || '');
                setLogoImageUrl(initialTheme.logoImageUrl || '');
                setSpriteBackUrl(initialTheme.spriteBackUrl || '');
                setSpriteFrontUrl(initialTheme.spriteFrontUrl || '');
                setSpriteShootUrl(initialTheme.spriteShootUrl || '');
                setSpriteVictoryUrl(initialTheme.spriteVictoryUrl || '');
                setBanner1536Url(initialTheme.banner1536Url || '');
                setBanner1920Url(initialTheme.banner1920Url || '');
            } else {
                setName('Nuevo Tema');
                setBackgroundImageUrl('');
                setLogoImageUrl('');
                setSpriteBackUrl('');
                setSpriteFrontUrl('');
                setSpriteShootUrl('');
                setSpriteVictoryUrl('');
                setBanner1536Url('');
                setBanner1920Url('');
            }
            setErrorMessage('');
            setSuccessMessage('');
            setUploadingField(null);
        }
    }, [isOpen, initialTheme]);

    // Subida de archivos individuales
    const handleFileUpload = useCallback(async (file, assetType, setterFn) => {
        if (!file) return;
        setUploadingField(assetType);
        setErrorMessage('');
        try {
            const result = await UploadAsset(file, assetType, roomId);
            if (result && result.success && result.url) {
                setterFn(result.url);
            } else {
                setErrorMessage(result?.message || `Error al subir el archivo para ${assetType}.`);
            }
        } catch (error) {
            console.error(`[QuickReactionPersonalThemeModal] Error al subir ${assetType}:`, error);
            setErrorMessage(`Error de red al procesar el archivo para ${assetType}.`);
        } finally {
            setUploadingField(null);
        }
    }, [roomId]);

    // Guardado de tema
    const handleSave = async (e) => {
        if (e) e.preventDefault();
        if (!name || !name.trim()) {
            setErrorMessage('El nombre del tema es obligatorio.');
            return;
        }

        setIsSaving(true);
        setErrorMessage('');
        setSuccessMessage('');

        const payload = {
            id: initialTheme?.id || undefined,
            roomId,
            name: name.trim(),
            backgroundImageUrl: backgroundImageUrl || null,
            logoImageUrl: logoImageUrl || null,
            spriteBackUrl: spriteBackUrl || null,
            spriteFrontUrl: spriteFrontUrl || null,
            spriteShootUrl: spriteShootUrl || null,
            spriteVictoryUrl: spriteVictoryUrl || null,
            banner1536Url: banner1536Url || null,
            banner1920Url: banner1920Url || null
        };

        try {
            const response = await SaveTheme(payload);
            if (response && response.success) {
                setSuccessMessage('Tema visual guardado exitosamente.');
                if (onSaved) onSaved(response.data || payload);
                setTimeout(() => {
                    onClose();
                }, 800);
            } else {
                setErrorMessage(response?.message || 'Error al persistir el tema visual.');
            }
        } catch (error) {
            console.error('[QuickReactionPersonalThemeModal] Error al guardar tema:', error);
            setErrorMessage('Error de conexión al guardar el tema.');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    const assetItems = [
        {
            key: 'background',
            label: 'Fondo Principal (1920x1080)',
            assetType: 'background',
            value: backgroundImageUrl,
            setter: setBackgroundImageUrl,
            help: 'Recomendado: 1920x1080 JPG/PNG/WebP'
        },
        {
            key: 'logo',
            label: 'Logo del Evento / Patrocinador',
            assetType: 'logo',
            value: logoImageUrl,
            setter: setLogoImageUrl,
            help: 'Recomendado: PNG con transparencia'
        },
        {
            key: 'sprite_back',
            label: 'Sprite 1 - Espalda (Modo Espera)',
            assetType: 'sprite_back',
            value: spriteBackUrl,
            setter: setSpriteBackUrl,
            help: 'Personaje de espaldas antes de la señal'
        },
        {
            key: 'sprite_front',
            label: 'Sprite 2 - Frente (Señal ¡FRENTE!)',
            assetType: 'sprite_front',
            value: spriteFrontUrl,
            setter: setSpriteFrontUrl,
            help: 'Personaje volteando al sonar la señal'
        },
        {
            key: 'sprite_shoot',
            label: 'Sprite 3 - Disparo / Acción Rápida',
            assetType: 'sprite_shoot',
            value: spriteShootUrl,
            setter: setSpriteShootUrl,
            help: 'Personaje disparando o ejecutando la acción'
        },
        {
            key: 'sprite_victory',
            label: 'Sprite 4 - Victoria / Celebración',
            assetType: 'sprite_victory',
            value: spriteVictoryUrl,
            setter: setSpriteVictoryUrl,
            help: 'Pose ganadora al completar con buen tiempo'
        },
        {
            key: 'banner_1536',
            label: 'Cintillo OBS 1536x240',
            assetType: 'banner_1536',
            value: banner1536Url,
            setter: setBanner1536Url,
            help: 'Gráfico inferior para pantallas 1536px'
        },
        {
            key: 'banner_1920',
            label: 'Cintillo OBS 1920x300',
            assetType: 'banner_1920',
            value: banner1920Url,
            setter: setBanner1920Url,
            help: 'Gráfico inferior Full HD para transmisión'
        }
    ];

    return (
        <div className="QrpModalOverlay" onClick={onClose}>
            <div
                className="QrpModal QrpModal--large"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header del Modal */}
                <div className="QrpModal__Header">
                    <div className="QrpModal__HeaderTitle">
                        <i className="bi bi-palette-fill" />
                        <span>{initialTheme ? 'Editar Tema Visual' : 'Nuevo Tema Visual'}</span>
                    </div>
                    <button
                        type="button"
                        className="QrpModal__CloseBtn"
                        onClick={onClose}
                        title="Cerrar modal"
                    >
                        <i className="bi bi-x-lg" />
                    </button>
                </div>

                {/* Body con Scroll Touch */}
                <div className="QrpModal__Body">
                    {errorMessage && (
                        <div className="QrpModal__Alert QrpModal__Alert--error">
                            <i className="bi bi-exclamation-octagon-fill" />
                            <span>{errorMessage}</span>
                        </div>
                    )}

                    {successMessage && (
                        <div className="QrpModal__Alert QrpModal__Alert--success">
                            <i className="bi bi-check-circle-fill" />
                            <span>{successMessage}</span>
                        </div>
                    )}

                    {/* Nombre del Tema */}
                    <div className="QrpModal__FieldGroup">
                        <label className="QrpModal__Label">
                            Nombre del Tema <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            className="QrpModal__Input"
                            placeholder="Ej. Tema Vaqueros del Oeste, Tema Neón..."
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={100}
                        />
                    </div>

                    {/* Grid de Assets Gráficos y Sprites */}
                    <div className="QrpModal__AssetsGrid">
                        {assetItems.map((item) => (
                            <div key={item.key} className="QrpModal__AssetCard">
                                <div className="QrpModal__AssetCardHeader">
                                    <span className="QrpModal__AssetCardTitle">{item.label}</span>
                                    <span className="QrpModal__AssetCardHelp">{item.help}</span>
                                </div>

                                {/* Preview de Imagen o Placeholder */}
                                <div className="QrpModal__AssetPreview">
                                    {item.value ? (
                                        <div className="QrpModal__PreviewImageWrapper">
                                            <img
                                                src={resolveAssetUrl(item.value)}
                                                alt={item.label}
                                                className="QrpModal__PreviewImg"
                                            />
                                            <button
                                                type="button"
                                                className="QrpModal__RemoveAssetBtn"
                                                onClick={() => item.setter('')}
                                                title="Eliminar este asset"
                                            >
                                                <i className="bi bi-trash-fill" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="QrpModal__Placeholder">
                                            <i className="bi bi-image" />
                                            <span>Sin asset configurado</span>
                                        </div>
                                    )}
                                </div>

                                {/* Controles de Carga */}
                                <div className="QrpModal__AssetControls">
                                    <label className="QrpModal__UploadBtn">
                                        {uploadingField === item.assetType ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" />
                                                <span>Subiendo...</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-cloud-arrow-up-fill me-1" />
                                                <span>Subir Archivo</span>
                                            </>
                                        )}
                                        <input
                                            type="file"
                                            accept="image/png,image/jpeg,image/webp,image/gif"
                                            style={{ display: 'none' }}
                                            disabled={uploadingField !== null}
                                            onChange={(e) => {
                                                const file = e.target.files && e.target.files[0];
                                                if (file) {
                                                    handleFileUpload(file, item.assetType, item.setter);
                                                }
                                            }}
                                        />
                                    </label>

                                    <input
                                        type="text"
                                        className="QrpModal__UrlInput"
                                        placeholder="O pega una URL directa..."
                                        value={item.value}
                                        onChange={(e) => item.setter(e.target.value)}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer con Acciones */}
                <div className="QrpModal__Footer">
                    <button
                        type="button"
                        className="QrpModal__Btn QrpModal__Btn--secondary"
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="QrpModal__Btn QrpModal__Btn--primary"
                        onClick={handleSave}
                        disabled={isSaving || uploadingField !== null}
                    >
                        {isSaving ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                <span>Guardando...</span>
                            </>
                        ) : (
                            <>
                                <i className="bi bi-check2-circle me-2" />
                                <span>Guardar Tema</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

QuickReactionPersonalThemeModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    roomId: PropTypes.string,
    initialTheme: PropTypes.object,
    onSaved: PropTypes.func
};

export default QuickReactionPersonalThemeModal;
